/**
 * Save a transcript as a plain-text file, whatever the file behind it is.
 *
 * **No backend of its own.** The transcript is already a query this app can page, so the file is
 * assembled in the browser out of exactly the turns the dialog renders — one implementation
 * behind both the ⋯ menu's item and the dialog's button, so what somebody downloads is what they
 * were reading. Nothing here is signed and nothing is fetched from storage: a `Blob` and an
 * anchor is what saving our own text is.
 *
 * The whole transcript has to land before a byte can be written, which is why this is a press
 * that ends in a file rather than a link the row can carry. A second press while the first is
 * still paging is refused silently — the item is already showing as busy, and a toast would
 * explain a state that is on screen.
 */

import type { Id } from "@repo/convex/dataModel";
import { useConvex } from "convex/react";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { resolveDocumentRefusalMessage } from "../lib/document-processing-view";
import {
  buildTranscriptText,
  groupTranscriptTurns,
  transcriptFileName,
} from "../lib/transcript-turns-view";
import { fetchTranscriptDocument } from "./use-transcript-document";

export type UseDownloadTranscriptResult = {
  /**
   * `processingRevisionId` names the transcription to save — a viewer opened on a citation saves
   * the transcript it is reading. Absent saves the one the document reads as now, which is what
   * the row's own menu item means.
   */
  downloadTranscript: (
    artifactId: string,
    name: string,
    processingRevisionId?: string | null
  ) => Promise<boolean>;
  isDownloadingTranscript: (artifactId: string) => boolean;
};

export function useDownloadTranscript(projectId: Id<"projects">): UseDownloadTranscriptResult {
  const convex = useConvex();
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  // A ref is the authority and the state is only its render — the rule
  // `use-pending-artifact-claims.ts` states, for the two presses that share one frame.
  const inFlight = useRef<Set<string>>(new Set());

  const downloadTranscript = useCallback(
    async (
      artifactId: string,
      name: string,
      processingRevisionId?: string | null
    ): Promise<boolean> => {
      if (inFlight.current.has(artifactId)) return false;
      inFlight.current.add(artifactId);
      setPendingIds((current) => new Set(current).add(artifactId));
      try {
        const result = await fetchTranscriptDocument(convex, {
          projectId,
          artifactId: artifactId as Id<"artifacts">,
          ...(processingRevisionId ? { processingRevisionId } : {}),
        });
        if (result === null) {
          toast.error(`Couldn't download the transcript of ${name} — the file is gone.`);
          return false;
        }
        saveTextFile(
          // The names ride on the same page the blocks came from, so a saved file names the
          // speakers exactly as the dialog did.
          buildTranscriptText(groupTranscriptTurns(result.blocks), result.page.speakerNames),
          transcriptFileName(result.page.name)
        );
        return true;
      } catch (error) {
        toast.error(
          resolveDocumentRefusalMessage(error, `Couldn't download the transcript of ${name}`)
        );
        return false;
      } finally {
        inFlight.current.delete(artifactId);
        setPendingIds((current) => {
          const next = new Set(current);
          next.delete(artifactId);
          return next;
        });
      }
    },
    [convex, projectId]
  );

  const isDownloadingTranscript = useCallback(
    (artifactId: string) => pendingIds.has(artifactId),
    [pendingIds]
  );

  return { downloadTranscript, isDownloadingTranscript };
}

/**
 * Hand the browser text to save.
 *
 * The object URL is revoked on the next frame rather than immediately: the click has to have been
 * dispatched before the blob behind it is released, and some browsers start the save
 * asynchronously.
 */
function saveTextFile(text: string, fileName: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
